import { useCallback, useMemo } from 'react';
import { STUDIO_SCRATCH_ID } from 'src/components/star-grid/hooks/useStudioSelection';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import type { StudioTemplateOption } from 'src/components/star-grid/reportStudio.types';
import { defaultVariant, familyOf, studioTemplateFamilyLabel, templateFamilies } from 'src/components/star-grid/utils/studioTemplateFamilies';
import { studioImportId, studioImportedReportId } from 'src/components/star-grid/utils/studioImportTemplate';

export interface StudioImportableReport {
  readonly id: number;
  readonly label: string;
}

export const useStudioTemplateChooser = (
  templates: readonly StudioTemplateOption[],
  selectedId: string,
  onChange: (id: string) => void,
  text: StarGridText,
  /** Saved reports offered as a starting point. Empty when editing, where copying makes no sense. */
  importable: readonly StudioImportableReport[] = []
) => {
  const families = useMemo(() => templateFamilies(templates), [templates]);
  const current = familyOf(families, selectedId);
  const choose = useCallback(
    (id: string) => {
      if (id === STUDIO_SCRATCH_ID) return onChange(id);
      // An import option is a report id, not a family, so it never resolves to a variant.
      if (studioImportedReportId(id) !== undefined) return onChange(id);
      if (id === current?.id) return;
      const family = families.find((item) => item.id === id);
      const template = family && defaultVariant(family);
      if (template) onChange(template.id);
    },
    [families, current?.id, onChange]
  );
  // One flat, grouped list: scratch, then each template, then the saved reports to copy from. Grouped
  // so "Import existing Report" reads as a heading rather than as another template.
  const groups = [
    {
      label: '',
      options: [
        { value: STUDIO_SCRATCH_ID, label: text('studioScratch') },
        ...families.map((family) => ({ value: family.id, label: studioTemplateFamilyLabel(family, text) }))
      ]
    },
    ...(importable.length
      ? [{ label: text('studioImportExisting'), options: importable.map((report) => ({ value: studioImportId(report.id), label: report.label })) }]
      : [])
  ];
  const importedId = studioImportedReportId(selectedId) !== undefined ? selectedId : undefined;
  return {
    current,
    // An imported report is the selection in its own right; otherwise the family, else scratch.
    value: importedId ?? current?.id ?? STUDIO_SCRATCH_ID,
    items: families.map((family) => ({ value: family.id, label: studioTemplateFamilyLabel(family, text) })),
    groups,
    choose
  };
};
